import React from "react";
import { useNavigate } from "react-router-dom";
import AddIcon from '../../assets/icons/Add.svg';
import ForwardArrowIcon from '../../assets/icons/ForwardArrow.svg';

interface Props {
  tableData: {
    columns: { name: string; accessor: string }[];
    data: {
      applications: any[];
    };
  };
}

const ApplicationTable: React.FC<Props> = ({ tableData }) => {
  const navigate = useNavigate();

  const handleRowClick = (appId: string) => {
    navigate(`/application/view-application/${appId}`);
  };

  // Helper to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} | ${hours}:${minutes}`;
  };

  // Helper to format type
  const formatType = (type: string) => {
    if (type === "undergrad") return "Under graduate";
    if (type === "postgrad") return "Post graduate";
    return type;
  };

  // Helper to capitalize the first letter of the current state
  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <div className="bg-white shadow rounded-[8px] py-[20px] px-4 md:px-[60px] mt-8">
      <div className="w-full">
        <div className="w-full flex flex-row justify-between items-center mb-6">
          <h2 className="text-[24px] font-ubuntu font-[600] mb-[2px]">
            My Applications
          </h2>
          <button
            onClick={() => navigate(`/application/new-application`)}
            className="flex flex-row gap-x-2 text-[14px] text-[#005C99] font-[700] cursor-pointer"
            aria-label="Create new application"
          >
            <p className="font-[400] font-ubuntu text-[15px] hidden md:block">
              New Application
            </p>
            <div>
              <img src={AddIcon} alt="Add new application" />
            </div>
          </button>
        </div>
        <hr className="border-[1px] border-gray-300 my-4" />
      </div>
        <table className="w-full">
          <thead>
            <tr>
              {tableData.columns.map((column, index) => (
                <th key={index} 
                className={`text-left ${
                  column.accessor === "schoolName" || column.accessor === "currentState"
                    ? "md:table-cell"
                    : "hidden md:table-cell"
                }`}
                >
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody >
            {tableData.data?.applications.map((row, index) => (
              <React.Fragment key={index}>
                <tr
                  className="cursor-pointer"
                  onClick={() => handleRowClick(row.applicationId)}
                  aria-label={`View details for application ${row.applicationId}`}
                >
                  {tableData.columns.map((column, colIndex) => {
                    const value =
                      column.accessor === "type"
                        ? formatType(row[column.accessor])
                        : column.accessor === "currentState"
                        ? capitalizeFirstLetter(row[column.accessor])
                        : column.accessor === "createdAt"
                        ? formatDate(row[column.accessor])
                        : row[column.accessor];
                    return (
                      <td
                        key={colIndex}
                        className={`text-left py-2 ${
                          column.accessor === "schoolName" || column.accessor === "currentState"
                            ? "md:table-cell"
                            : "hidden md:table-cell"
                        }`}
                      >
                        {value}
                      </td>
                    );
                  })}
                  <td className="flex items-center justify-center w-full">
                    <img src={ForwardArrowIcon} alt="View application details" />
                  </td>
                </tr>
                {index !== (tableData.data?.applications.length ?? 0) - 1 && (
                  <tr>
                    <td colSpan={tableData.columns.length + 1}>
                      <hr className="border-[1px] border-gray-200 my-3" />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
    </div>
  );
};

export default ApplicationTable;
